import React, { useState, useEffect } from 'react';
import './App.css';
import HospitableService from './services/hospitableService';
import ReservationList from './components/ReservationList';
import Settings from './components/Settings';
import './components/Settings.css';
import { Reservation } from './types/hospitable';
import { AlertCircle, CheckCircle, Settings as SettingsIcon } from 'lucide-react';

function App() {
  const [showSettings, setShowSettings] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Charger les données depuis le localStorage au démarrage
  useEffect(() => {
    const savedApiKey = localStorage.getItem('hospitableApiKey') || '';
    const savedCompanyInfo = localStorage.getItem('companyInfo');
    
    setApiKey(savedApiKey);
    
    if (savedCompanyInfo) {
      setCompanyInfo(JSON.parse(savedCompanyInfo));
    }
    
    // Si aucune configuration n'existe, ouvrir automatiquement les paramètres
    if (!savedApiKey || !savedCompanyInfo) {
      setShowSettings(true);
    }
  }, []);

  // Fonction pour gérer la sauvegarde depuis Settings
  const handleSettingsSave = (newApiKey: string, newCompanyInfo: any) => {
    setApiKey(newApiKey);
    setCompanyInfo(newCompanyInfo);
    setShowSettings(false);
    setSuccess('Paramètres sauvegardés avec succès');
  };

  const fetchReservations = async () => {
    if (!apiKey) {
      setError('Veuillez entrer votre clé API Hospitable');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const service = new HospitableService({ apiKey });
      const data = await service.getReservations(startDate, endDate);
      setReservations(data);
      setSuccess(`${data.length} réservations récupérées avec succès`);
    } catch (err: any) {
      setError(`Erreur lors de la récupération des réservations: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="app">
      <div className="app-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>Hospitable Invoice Generator</h1>
            <p>Générez des factures PDF pour vos réservations Hospitable</p>
          </div>
          <button
            onClick={() => setShowSettings(true)}
            className="settings-button"
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 20px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '1rem',
              transition: 'background 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <SettingsIcon size={20} />
            Paramètres
          </button>
        </div>
      </div>

      {/* Afficher les paramètres */}
      {showSettings && (
        <Settings
          onClose={() => setShowSettings(false)}
          onSave={handleSettingsSave}
        />
      )}

      {/* Afficher un avertissement si la configuration est incomplète */}
      {(!apiKey || !companyInfo) && (
        <div className="api-config">
          <div className="error-message">
            <AlertCircle size={20} />
            Configuration incomplète. <button onClick={() => setShowSettings(true)} style={{ textDecoration: 'underline', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>Cliquez ici pour configurer l'application</button>
          </div>
        </div>
      )}

      <div className="api-config">
        <h2>Recherche de réservations</h2>

        <div className="date-filters">
          <div className="form-group">
            <label htmlFor="startDate">Date de début</label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="endDate">Date de fin</label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <button
          className="primary-button"
          onClick={fetchReservations}
          disabled={loading || !apiKey}
        >
          {loading ? 'Chargement...' : 'Récupérer les réservations'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          {error}
        </div>
      )}
      
      {success && (
        <div className="success-message">
          <CheckCircle size={20} />
          {success}
        </div>
      )}

      <div className="content">
        {reservations.length > 0 && (
          <>
            <ReservationList
              reservations={reservations}
              companyInfo={companyInfo}
            />

          </>
        )}
      </div>
    </div>
  );
}

export default App;